const VALUE = { p: 100, n: 320, b: 330, r: 500, q: 900, k: 0 };

const PAWN_PST = [
  [0, 0, 0, 0, 0, 0, 0, 0],
  [50, 50, 50, 50, 50, 50, 50, 50],
  [10, 10, 20, 30, 30, 20, 10, 10],
  [5, 5, 10, 25, 25, 10, 5, 5],
  [0, 0, 0, 20, 20, 0, 0, 0],
  [5, -5, -10, 0, 0, -10, -5, 5],
  [5, 10, 10, -20, -20, 10, 10, 5],
  [0, 0, 0, 0, 0, 0, 0, 0],
];

const KNIGHT_PST = [
  [-50, -40, -30, -30, -30, -30, -40, -50],
  [-40, -20, 0, 0, 0, 0, -20, -40],
  [-30, 0, 10, 15, 15, 10, 0, -30],
  [-30, 5, 15, 20, 20, 15, 5, -30],
  [-30, 0, 15, 20, 20, 15, 0, -30],
  [-30, 5, 10, 15, 15, 10, 5, -30],
  [-40, -20, 0, 5, 5, 0, -20, -40],
  [-50, -40, -30, -30, -30, -30, -40, -50],
];

const ROOK_DIRS = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];
const BISHOP_DIRS = [
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];
const KNIGHT_DELTAS = [
  [2, 1],
  [2, -1],
  [-2, 1],
  [-2, -1],
  [1, 2],
  [1, -2],
  [-1, 2],
  [-1, -2],
];

const onBoard = (r, c) => r >= 0 && r < 8 && c >= 0 && c < 8;

const colorOf = (piece) => {
  if (!piece) return null;
  return piece === piece.toUpperCase() ? "w" : "b";
};

const enemyOf = (color) => (color === "w" ? "b" : "w");

const sameMove = (a, b) =>
  a.from.r === b.from.r &&
  a.from.c === b.from.c &&
  a.to.r === b.to.r &&
  a.to.c === b.to.c &&
  (a.promotion || null) === (b.promotion || null);

const algebraic = (r, c) => "abcdefgh"[c] + String(8 - r);

function isSquareAttacked(board, r, c, byColor) {
  if (byColor === "w") {
    for (const dc of [-1, 1]) {
      const pr = r + 1;
      const pc = c + dc;
      if (onBoard(pr, pc) && board[pr][pc] === "P") return true;
    }
  } else {
    for (const dc of [-1, 1]) {
      const pr = r - 1;
      const pc = c + dc;
      if (onBoard(pr, pc) && board[pr][pc] === "p") return true;
    }
  }

  for (const [dr, dc] of KNIGHT_DELTAS) {
    const nr = r + dr;
    const nc = c + dc;
    if (!onBoard(nr, nc)) continue;
    const piece = board[nr][nc];
    if (piece && colorOf(piece) === byColor && piece.toLowerCase() === "n") {
      return true;
    }
  }

  for (const [dr, dc] of ROOK_DIRS) {
    let nr = r + dr;
    let nc = c + dc;
    while (onBoard(nr, nc)) {
      const piece = board[nr][nc];
      if (piece) {
        if (
          colorOf(piece) === byColor &&
          (piece.toLowerCase() === "r" || piece.toLowerCase() === "q")
        ) {
          return true;
        }
        break;
      }
      nr += dr;
      nc += dc;
    }
  }

  for (const [dr, dc] of BISHOP_DIRS) {
    let nr = r + dr;
    let nc = c + dc;
    while (onBoard(nr, nc)) {
      const piece = board[nr][nc];
      if (piece) {
        if (
          colorOf(piece) === byColor &&
          (piece.toLowerCase() === "b" || piece.toLowerCase() === "q")
        ) {
          return true;
        }
        break;
      }
      nr += dr;
      nc += dc;
    }
  }

  for (let dr = -1; dr <= 1; dr += 1) {
    for (let dc = -1; dc <= 1; dc += 1) {
      if (dr === 0 && dc === 0) continue;
      const nr = r + dr;
      const nc = c + dc;
      if (!onBoard(nr, nc)) continue;
      const piece = board[nr][nc];
      if (piece && colorOf(piece) === byColor && piece.toLowerCase() === "k") {
        return true;
      }
    }
  }

  return false;
}

function findKing(board, color) {
  const king = color === "w" ? "K" : "k";
  for (let r = 0; r < 8; r += 1) {
    for (let c = 0; c < 8; c += 1) {
      if (board[r][c] === king) return { r, c };
    }
  }
  return null;
}

function isInCheck(state, color) {
  const king = findKing(state.board, color);
  if (!king) return true;
  return isSquareAttacked(state.board, king.r, king.c, enemyOf(color));
}

function pushPawn(moves, r, c, tr, tc, promo) {
  if (promo) {
    for (const promotion of ["q", "r", "b", "n"]) {
      moves.push({ from: { r, c }, to: { r: tr, c: tc }, promotion });
    }
    return;
  }
  moves.push({ from: { r, c }, to: { r: tr, c: tc }, promotion: null });
}

function addPawns(state, moves) {
  const dir = state.turn === "w" ? -1 : 1;
  const startRow = state.turn === "w" ? 6 : 1;
  const piece = state.turn === "w" ? "P" : "p";

  for (let r = 0; r < 8; r += 1) {
    for (let c = 0; c < 8; c += 1) {
      if (state.board[r][c] !== piece) continue;
      const oneR = r + dir;
      if (!onBoard(oneR, c)) continue;

      if (!state.board[oneR][c]) {
        pushPawn(moves, r, c, oneR, c, oneR === 0 || oneR === 7);
        const twoR = r + dir * 2;
        if (r === startRow && onBoard(twoR, c) && !state.board[twoR][c]) {
          moves.push({
            from: { r, c },
            to: { r: twoR, c },
            promotion: null,
          });
        }
      }

      for (const dc of [-1, 1]) {
        const nc = c + dc;
        if (!onBoard(oneR, nc)) continue;
        const target = state.board[oneR][nc];
        const ep =
          state.ep && state.ep.r === oneR && state.ep.c === nc && !target;
        if ((target && colorOf(target) !== state.turn) || ep) {
          pushPawn(moves, r, c, oneR, nc, oneR === 0 || oneR === 7);
        }
      }
    }
  }
}

function addSlider(state, moves, r, c, dirs) {
  for (const [dr, dc] of dirs) {
    let nr = r + dr;
    let nc = c + dc;
    while (onBoard(nr, nc)) {
      const target = state.board[nr][nc];
      if (!target) {
        moves.push({ from: { r, c }, to: { r: nr, c: nc }, promotion: null });
      } else {
        if (colorOf(target) !== state.turn) {
          moves.push({ from: { r, c }, to: { r: nr, c: nc }, promotion: null });
        }
        break;
      }
      nr += dr;
      nc += dc;
    }
  }
}

function addCastles(state, moves) {
  const row = state.turn === "w" ? 7 : 0;
  const king = state.turn === "w" ? "K" : "k";
  const rook = state.turn === "w" ? "R" : "r";
  if (state.board[row][4] !== king) return;
  if (isInCheck(state, state.turn)) return;

  const enemy = enemyOf(state.turn);
  const kingSide = state.turn === "w" ? state.castling.wK : state.castling.bK;
  const queenSide = state.turn === "w" ? state.castling.wQ : state.castling.bQ;

  if (
    kingSide &&
    state.board[row][7] === rook &&
    !state.board[row][5] &&
    !state.board[row][6] &&
    !isSquareAttacked(state.board, row, 5, enemy) &&
    !isSquareAttacked(state.board, row, 6, enemy)
  ) {
    moves.push({
      from: { r: row, c: 4 },
      to: { r: row, c: 6 },
      promotion: null,
    });
  }

  if (
    queenSide &&
    state.board[row][0] === rook &&
    !state.board[row][1] &&
    !state.board[row][2] &&
    !state.board[row][3] &&
    !isSquareAttacked(state.board, row, 3, enemy) &&
    !isSquareAttacked(state.board, row, 2, enemy)
  ) {
    moves.push({
      from: { r: row, c: 4 },
      to: { r: row, c: 2 },
      promotion: null,
    });
  }
}

function pseudoMoves(state) {
  const moves = [];
  addPawns(state, moves);

  for (let r = 0; r < 8; r += 1) {
    for (let c = 0; c < 8; c += 1) {
      const piece = state.board[r][c];
      if (!piece || colorOf(piece) !== state.turn) continue;
      const kind = piece.toLowerCase();
      if (kind === "n") {
        for (const [dr, dc] of KNIGHT_DELTAS) {
          const nr = r + dr;
          const nc = c + dc;
          if (!onBoard(nr, nc)) continue;
          const target = state.board[nr][nc];
          if (!target || colorOf(target) !== state.turn) {
            moves.push({
              from: { r, c },
              to: { r: nr, c: nc },
              promotion: null,
            });
          }
        }
      } else if (kind === "b") {
        addSlider(state, moves, r, c, BISHOP_DIRS);
      } else if (kind === "r") {
        addSlider(state, moves, r, c, ROOK_DIRS);
      } else if (kind === "q") {
        addSlider(state, moves, r, c, ROOK_DIRS);
        addSlider(state, moves, r, c, BISHOP_DIRS);
      } else if (kind === "k") {
        for (let dr = -1; dr <= 1; dr += 1) {
          for (let dc = -1; dc <= 1; dc += 1) {
            if (dr === 0 && dc === 0) continue;
            const nr = r + dr;
            const nc = c + dc;
            if (!onBoard(nr, nc)) continue;
            const target = state.board[nr][nc];
            if (!target || colorOf(target) !== state.turn) {
              moves.push({
                from: { r, c },
                to: { r: nr, c: nc },
                promotion: null,
              });
            }
          }
        }
      }
    }
  }

  addCastles(state, moves);
  return moves;
}

function clearHomeRook(castling, r, c) {
  if (r === 7 && c === 0) castling.wQ = false;
  if (r === 7 && c === 7) castling.wK = false;
  if (r === 0 && c === 0) castling.bQ = false;
  if (r === 0 && c === 7) castling.bK = false;
}

function execute(state, move) {
  const piece = state.board[move.from.r][move.from.c];
  if (!piece || colorOf(piece) !== state.turn) return null;

  const board = state.board.map((row) => row.slice());
  const castling = { ...state.castling };
  const dest = board[move.to.r][move.to.c];
  const isPawn = piece.toLowerCase() === "p";
  let ep = null;

  if (
    isPawn &&
    state.ep &&
    move.to.r === state.ep.r &&
    move.to.c === state.ep.c &&
    !dest
  ) {
    const capR = state.turn === "w" ? move.to.r + 1 : move.to.r - 1;
    board[capR][move.to.c] = null;
  } else if (dest) {
    if (dest.toLowerCase() === "r") clearHomeRook(castling, move.to.r, move.to.c);
  }

  board[move.to.r][move.to.c] = piece;
  board[move.from.r][move.from.c] = null;

  if (piece.toLowerCase() === "k" && Math.abs(move.to.c - move.from.c) === 2) {
    const row = move.from.r;
    if (move.to.c === 6) {
      board[row][5] = board[row][7];
      board[row][7] = null;
    } else if (move.to.c === 2) {
      board[row][3] = board[row][0];
      board[row][0] = null;
    }
  }

  if (isPawn && (move.to.r === 0 || move.to.r === 7)) {
    const kind = (move.promotion || "q").toLowerCase();
    if (!"qrbn".includes(kind)) return null;
    board[move.to.r][move.to.c] = state.turn === "w" ? kind.toUpperCase() : kind;
  }

  if (piece === "K") {
    castling.wK = false;
    castling.wQ = false;
  } else if (piece === "k") {
    castling.bK = false;
    castling.bQ = false;
  } else if (piece === "R" && move.from.r === 7 && move.from.c === 7) {
    castling.wK = false;
  } else if (piece === "R" && move.from.r === 7 && move.from.c === 0) {
    castling.wQ = false;
  } else if (piece === "r" && move.from.r === 0 && move.from.c === 7) {
    castling.bK = false;
  } else if (piece === "r" && move.from.r === 0 && move.from.c === 0) {
    castling.bQ = false;
  }

  if (isPawn && Math.abs(move.to.r - move.from.r) === 2) {
    ep = { r: (move.to.r + move.from.r) / 2, c: move.from.c };
  }

  return {
    board,
    turn: enemyOf(state.turn),
    castling,
    ep,
    status: "playing",
    winner: null,
    reason: null,
    inCheck: false,
    lastMove: null,
    ply: (state.ply || 0) + 1,
  };
}

function isInsufficient(board) {
  const extras = [];
  for (let r = 0; r < 8; r += 1) {
    for (let c = 0; c < 8; c += 1) {
      const piece = board[r][c];
      if (!piece || piece.toLowerCase() === "k") continue;
      extras.push(piece.toLowerCase());
    }
  }
  if (extras.length === 0) return true;
  if (extras.length === 1 && (extras[0] === "n" || extras[0] === "b")) return true;
  return false;
}

function legalMovesDetailed(state) {
  if (!state || state.status === "checkmate" || state.status === "stalemate" || state.status === "draw" || state.status === "resigned") {
    return [];
  }
  const out = [];
  for (const move of pseudoMoves(state)) {
    const next = execute(state, move);
    if (!next) continue;
    if (isInCheck(next, state.turn)) continue;
    out.push({ move, next });
  }
  return out;
}

function withStatus(state) {
  const detailed = legalMovesDetailed({ ...state, status: "playing" });
  const inCheck = isInCheck(state, state.turn);
  if (detailed.length === 0) {
    state.status = inCheck ? "checkmate" : "stalemate";
    state.winner = inCheck ? enemyOf(state.turn) : null;
    state.reason = inCheck ? "checkmate" : "stalemate";
  } else if (isInsufficient(state.board)) {
    state.status = "draw";
    state.winner = null;
    state.reason = "insufficient";
  } else {
    state.status = "playing";
    state.winner = null;
    state.reason = null;
  }
  state.inCheck = inCheck && state.status === "playing";
  return state;
}

function createInitialState() {
  return withStatus({
    board: [
      ["r", "n", "b", "q", "k", "b", "n", "r"],
      ["p", "p", "p", "p", "p", "p", "p", "p"],
      [null, null, null, null, null, null, null, null],
      [null, null, null, null, null, null, null, null],
      [null, null, null, null, null, null, null, null],
      [null, null, null, null, null, null, null, null],
      ["P", "P", "P", "P", "P", "P", "P", "P"],
      ["R", "N", "B", "Q", "K", "B", "N", "R"],
    ],
    turn: "w",
    castling: { wK: true, wQ: true, bK: true, bQ: true },
    ep: null,
    status: "playing",
    winner: null,
    reason: null,
    inCheck: false,
    lastMove: null,
    ply: 0,
  });
}

function applyMove(state, move) {
  if (!state || state.status !== "playing") return null;
  const found = legalMovesDetailed(state).find(({ move: candidate }) =>
    sameMove(candidate, move)
  );
  if (!found) return null;
  const done = withStatus(found.next);
  done.lastMove = { from: { ...found.move.from }, to: { ...found.move.to } };
  return done;
}

function legalMoves(state) {
  return legalMovesDetailed(state).map(({ move }) => move);
}

function material(state) {
  let score = 0;
  let whiteBishops = 0;
  let blackBishops = 0;
  for (let r = 0; r < 8; r += 1) {
    for (let c = 0; c < 8; c += 1) {
      const piece = state.board[r][c];
      if (!piece) continue;
      const kind = piece.toLowerCase();
      const white = colorOf(piece) === "w";
      let val = VALUE[kind];
      if (kind === "p") val += PAWN_PST[white ? r : 7 - r][c];
      if (kind === "n") val += KNIGHT_PST[white ? r : 7 - r][c];
      if (kind === "b") {
        if (white) whiteBishops += 1;
        else blackBishops += 1;
      }
      score += white ? val : -val;
    }
  }
  if (whiteBishops >= 2) score += 30;
  if (blackBishops >= 2) score -= 30;
  return score;
}

function moveOrder(state, move) {
  const victim = state.board[move.to.r][move.to.c];
  const attacker = state.board[move.from.r][move.from.c];
  let score = 0;
  if (victim) {
    score += VALUE[victim.toLowerCase()] * 10 - VALUE[attacker.toLowerCase()];
  }
  if (move.promotion === "q") score += 800;
  else if (move.promotion) score += 400;
  return score;
}

function search(state, depth, alpha, beta) {
  if (isInsufficient(state.board)) return 0;
  const detailed = legalMovesDetailed(state);
  if (detailed.length === 0) {
    if (isInCheck(state, state.turn)) {
      const mate = 100000 + depth;
      return state.turn === "w" ? -mate : mate;
    }
    return 0;
  }
  if (depth === 0) return material(state);

  detailed.sort((a, b) => moveOrder(state, b.move) - moveOrder(state, a.move));

  if (state.turn === "w") {
    let best = -Infinity;
    for (const { next } of detailed) {
      const score = search(next, depth - 1, alpha, beta);
      if (score > best) best = score;
      if (score > alpha) alpha = score;
      if (alpha >= beta) break;
    }
    return best;
  }

  let best = Infinity;
  for (const { next } of detailed) {
    const score = search(next, depth - 1, alpha, beta);
    if (score < best) best = score;
    if (score < beta) beta = score;
    if (alpha >= beta) break;
  }
  return best;
}

function chooseAiMove(state, level) {
  const detailed = legalMovesDetailed(state);
  if (!detailed.length) return null;
  if (level === "beginner") {
    return detailed[Math.floor(Math.random() * detailed.length)].move;
  }
  if (level === "easy") {
    const maximizing = state.turn === "w";
    const scored = detailed.map(({ move, next }) => ({
      move,
      score: material(next) + (Math.random() - 0.5) * 180,
    }));
    scored.sort((a, b) => (maximizing ? b.score - a.score : a.score - b.score));
    return scored[0].move;
  }

  const depth = level === "master" ? 4 : level === "hard" ? 3 : 2;
  const slack = level === "intermediate" ? 140 : 0;
  const maximizing = state.turn === "w";
  let bestScore = maximizing ? -Infinity : Infinity;
  const scored = [];
  for (const { move, next } of detailed) {
    const score = search(next, depth - 1, -Infinity, Infinity);
    scored.push({ move, score });
    if (maximizing) bestScore = Math.max(bestScore, score);
    else bestScore = Math.min(bestScore, score);
  }
  const pool = scored.filter((entry) => Math.abs(entry.score - bestScore) <= slack);
  return pool[Math.floor(Math.random() * pool.length)].move;
}

function fromFen(fen) {
  const [placement, turn, castling, ep] = fen.trim().split(/\s+/);
  const board = placement.split("/").map((row) => {
    const line = [];
    for (const ch of row) {
      if (/\d/.test(ch)) {
        for (let i = 0; i < Number(ch); i += 1) line.push(null);
      } else {
        line.push(ch);
      }
    }
    return line;
  });
  const epSquare =
    !ep || ep === "-"
      ? null
      : { c: "abcdefgh".indexOf(ep[0]), r: 8 - Number(ep[1]) };
  return withStatus({
    board,
    turn,
    castling: {
      wK: castling.includes("K"),
      wQ: castling.includes("Q"),
      bK: castling.includes("k"),
      bQ: castling.includes("q"),
    },
    ep: epSquare,
    status: "playing",
    winner: null,
    reason: null,
    inCheck: false,
    lastMove: null,
    ply: 0,
  });
}

function perft(state, depth) {
  if (depth === 0) return 1;
  const detailed = legalMovesDetailed(state);
  if (depth === 1) return detailed.length;
  let total = 0;
  for (const { next } of detailed) total += perft(next, depth - 1);
  return total;
}

function snapshot(state) {
  return {
    board: state.board.map((row) => row.slice()),
    turn: state.turn,
    castling: { ...state.castling },
    ep: state.ep ? { ...state.ep } : null,
    status: state.status,
    winner: state.winner,
    reason: state.reason,
    inCheck: state.inCheck,
    ply: state.ply || 0,
    lastMove: state.lastMove
      ? {
          from: { ...state.lastMove.from },
          to: { ...state.lastMove.to },
        }
      : null,
  };
}

function fromSnapshot(data) {
  return {
    board: data.board.map((row) => row.slice()),
    turn: data.turn,
    castling: { ...data.castling },
    ep: data.ep ? { ...data.ep } : null,
    status: data.status,
    winner: data.winner || null,
    reason: data.reason || null,
    inCheck: Boolean(data.inCheck),
    ply: data.ply || 0,
    lastMove: data.lastMove
      ? {
          from: { ...data.lastMove.from },
          to: { ...data.lastMove.to },
        }
      : null,
  };
}

const chess = {
  algebraic,
  applyMove,
  chooseAiMove,
  colorOf,
  createInitialState,
  fromFen,
  fromSnapshot,
  isInCheck,
  legalMoves,
  perft,
  snapshot,
};

export {
  algebraic,
  applyMove,
  chooseAiMove,
  colorOf,
  createInitialState,
  fromFen,
  fromSnapshot,
  isInCheck,
  legalMoves,
  perft,
  snapshot,
};
export default chess;
